import { useState, useCallback, useMemo, useRef, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarRange, ChevronLeft, ChevronRight, Users, Users2, Gauge, Trello } from 'lucide-react';
import { api } from '../../core/apiClient';
import ProjectsKanbanBoard, { type KanbanProject, type KanbanBrand } from './ProjectsKanbanBoard';

interface EmployeeOption {
  id: string;
  firstName: string;
  lastName: string;
  departamentEnum?: string;
  weeklyHours?: number;
}

interface TallerProjectOption {
  id: string;
  expedientNumber?: number;
  name: string;
  etiquetaExpedient?: string | null;
}

interface GanttOT {
  id: string;
  orderNumber: string;
  name: string;
  type?: string;
  status: string;
  prevHours: number;
  assignedToId?: string | null;
  plannedStartDate?: string | null;
  plannedEndDate?: string | null;
  projectId?: string;
  project?: { expedientNumber?: number; name?: string; etiquetaExpedient?: string | null };
  node?: { code?: string | null; name?: string };
}

// Assignació OT↔operari de "Planificació > Per operari" (2026-10-06) — font de veritat del
// Gantt per operari: una OT pot tenir vàries files (una per operari) en paral·lel, independent
// de GanttOT.assignedToId (que es manté a altres pantalles com a "responsable principal").
interface GanttAssignment {
  id: string;
  workOrderId: string;
  employeeId: string;
  plannedStartDate: string;
  plannedEndDate: string;
  // Nom opcional de fase (2026-10-08) — una mateixa OT pot tenir vàries assignacions per al
  // mateix operari en dates diferents (p.ex. "Preparació"/"Acabats").
  phaseName?: string | null;
}

// ── Gantt per projecte: 4 fases (mateix codis reals que costAnalysis.service.ts —
// convertirAEP crea sempre OT's MAT/SRV/TRE/MUN en acceptar un pressupost; general/taller/
// muntatge/fabricacio... nomes apareixen si algu crea una OT a ma). Compres fusiona
// Materials (MAT) i Subcontractes (SRV): totes dues son fases de compra/contractacio.
type FaseKey = 'OFICINA' | 'COMPRES' | 'TALLER' | 'MUNTATGE';
const FASE_OF: Record<string, FaseKey> = {
  general: 'OFICINA', OFI: 'OFICINA',
  MAT: 'COMPRES', SRV: 'COMPRES',
  TRE: 'TALLER', taller: 'TALLER', fabricacio: 'TALLER', preparacio: 'TALLER', fustes: 'TALLER', estructures: 'TALLER', production: 'TALLER',
  MUN: 'MUNTATGE', muntatge: 'MUNTATGE', assembly: 'MUNTATGE',
};

// ── Capacitat de recursos: 3 equips (mateixos codis que Departament a Employee.ts —
// oficina/taller/muntatge). Nomes compten les fases de mà d'obra (no Compres/materials).
type EquipKey = 'OFICINA' | 'TALLER' | 'MUNTATGE';
const EQUIP_LABEL: Record<EquipKey, string> = {
  OFICINA: 'Oficina tècnica', TALLER: 'Taller fabricació', MUNTATGE: 'Equip muntatge',
};
const EQUIP_OF_DEPARTAMENT: Record<string, EquipKey> = { oficina: 'OFICINA', taller: 'TALLER', muntatge: 'MUNTATGE' };
const WEEKS_VISIBLE = 6;

// Dilluns de la setmana ISO que conté la data donada.
const mondayOfWeek = (iso: string): string => {
  const d = new Date(iso + 'T00:00:00');
  const day = d.getDay(); // 0=diumenge..6=dissabte
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return toISO(d);
};
const isoWeekLabel = (mondayIso: string): string => {
  const d = new Date(mondayIso + 'T00:00:00');
  const onejan = new Date(d.getFullYear(), 0, 1);
  const week = Math.ceil(((d.getTime() - onejan.getTime()) / 86400000 + onejan.getDay() + 1) / 7);
  return `S${week}`;
};

// ── Gantt: helpers de dates (nomes la part de data, sense hora/TZ) ─────────
// toISOString() converteix a UTC — amb el navegador en zona horària per
// davant d'UTC (p.ex. Espanya, UTC+1/+2), la mitjanit local "cau" al dia
// anterior en UTC i slice(0,10) es queda amb aquesta data anterior. Per
// aixo es formata amb els getters LOCALS (getFullYear/getMonth/getDate),
// mai amb toISOString(), perque addDaysISO/toISO sempre coincideixin amb
// el dia que l'usuari veu i selecciona a la graella.
const toISO = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addDaysISO = (iso: string, n: number): string => {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return toISO(d);
};
const diffDaysISO = (a: string, b: string): number =>
  Math.round((new Date(b + 'T00:00:00').getTime() - new Date(a + 'T00:00:00').getTime()) / 86400000);

// Normalitza a 'YYYY-MM-DD': si el backend retorna la data amb hora/zona
// inclosa (p.ex. "2026-10-21T00:00:00.000Z" en lloc de "2026-10-21" — pot
// passar segons com el driver de MariaDB hidrati una columna DATE), les
// comparacions de text que fa tot aquest fitxer (ordenar, detectar
// solapaments, filtrar per rang visible) fallen de forma imprevisible
// nomes per als registres afectats, perque "2026-10-21" < "2026-10-21T..."
// sempre és true en comparar strings encara que sigui el mateix dia.
const onlyDate = (v: string | null | undefined): string => (v ? v.slice(0, 10) : '');

const VISIBLE_DAYS = 21; // tres setmanes completes
const EMPLOYEE_COL_WIDTH = 160; // px
const WEEKDAY_LABELS = ['Dg', 'Dl', 'Dt', 'Dc', 'Dj', 'Dv', 'Ds'];
const MONTH_LABELS = ['Gen', 'Feb', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Oct', 'Nov', 'Des'];

// Alçada de cada "carril" dins una fila del Gantt (barra de 32px + 4px de
// marge) i el padding superior/inferior de la fila — s'usen per apilar les
// barres que se superposen en comptes de dibuixar-les una sobre l'altra.
const GANTT_LANE_HEIGHT = 36;
const GANTT_ROW_PAD = 6;

// Assigna un "carril" (0, 1, 2...) a cada element perque els que se
// superposen en dates no quedin mai al mateix carril — algorisme greedy
// d'interval scheduling: ordena per data d'inici i reaprofiteix el primer
// carril lliure (el que la seva darrera data de fi ja ha passat).
const assignLanes = <T,>(items: T[], getStart: (item: T) => string, getEnd: (item: T) => string): number[] => {
  const order = items
    .map((item, idx) => ({ idx, start: getStart(item), end: getEnd(item) }))
    .sort((a, b) => a.start.localeCompare(b.start) || a.end.localeCompare(b.end));
  const laneEnds: string[] = [];
  const laneOf = new Array(items.length).fill(0);
  order.forEach(({ idx, start, end }) => {
    let lane = laneEnds.findIndex((laneEnd) => laneEnd < start);
    if (lane === -1) { lane = laneEnds.length; laneEnds.push(end); }
    else { laneEnds[lane] = end; }
    laneOf[idx] = lane;
  });
  return laneOf;
};

export default function Planificacio() {
  const navigate = useNavigate();
  const [viewBy, setViewBy] = useState<'operari' | 'operari2' | 'capacitat' | 'kanban'>('operari');
  const [allEmployees, setAllEmployees] = useState<EmployeeOption[]>([]);
  const [allOTs, setAllOTs] = useState<GanttOT[]>([]);
  const [assignments, setAssignments] = useState<GanttAssignment[]>([]);
  // Expedients de taller actius — font de veritat de les files d'"Operaris 2" (2026-10-12),
  // independent de ganttOTs: un expedient hi ha d'aparèixer encara que no tingui (encara) cap
  // OT de Treball/Muntatge creada, per poder-hi arrossegar operaris igualment.
  const [allTallerProjects, setAllTallerProjects] = useState<TallerProjectOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [rangeStart, setRangeStart] = useState<string>(() => toISO(new Date()));

  // fetchAll(silent) — silent=true evita passar per l'estat "Carregant..."
  // (que col·lapsa tota la pantalla i fa que el navegador perdi la posició
  // de scroll) quan nomes es refresca despres d'una acció (drag/resize).
  const fetchAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [employeesRes, ganttRes, assignmentsRes, projectsRes] = await Promise.all([
        api.get<{ success: boolean; data: EmployeeOption[] }>('/employees?status=active'),
        api.get<{ success: boolean; data: GanttOT[] }>('/work-orders'),
        api.get<{ success: boolean; data: GanttAssignment[] }>('/work-orders/assignments'),
        api.get<{ success: boolean; data: any[] }>('/taller/projects', { status: 'active', limit: 500 }),
      ]);
      // Es desen tots els actius (Oficina inclosa) — cada vista en filtra el que necessita:
      // "per operari" nomes Taller/Muntatge (son els assignables a OT), "capacitat" els 3 equips.
      setAllEmployees(employeesRes.data || []);
      // Es desa la llista sencera (tots els tipus d'OT) — cada vista (per operari / per
      // projecte) en filtra i n'agrupa el que necessita.
      setAllOTs((ganttRes.data || [])
        .filter((ot) => ot.status !== 'completed' && ot.status !== 'cancelled')
        .map((ot) => ({ ...ot, plannedStartDate: ot.plannedStartDate ? onlyDate(ot.plannedStartDate) : ot.plannedStartDate, plannedEndDate: ot.plannedEndDate ? onlyDate(ot.plannedEndDate) : ot.plannedEndDate })));
      setAssignments((assignmentsRes.data || []).map((a) => ({ ...a, plannedStartDate: onlyDate(a.plannedStartDate), plannedEndDate: onlyDate(a.plannedEndDate) })));
      // /taller/projects respon amb columnes en snake_case (query SQL directa, no entitat
      // TypeORM) — es normalitzen aquí mateix.
      setAllTallerProjects(
        (projectsRes.data || []).map((p) => ({
          id: p.id,
          expedientNumber: p.expedientNumber ?? p.expedient_number,
          name: p.name,
          etiquetaExpedient: p.etiquetaExpedient ?? p.etiqueta_expedient ?? null,
        }))
      );
    } catch (error) {
      console.error('Error carregant Planificació:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Vista "Kanban" — moguda aquí des d'Expedients.tsx (2026-10-08). Dades independents de
  // fetchAll (projectes+marques, no OT's/empleats) — es carreguen nomes la primera vegada
  // que l'usuari obre aquesta pestanya, per no penalitzar les altres vistes.
  const [kanbanProjects, setKanbanProjects] = useState<KanbanProject[]>([]);
  const [kanbanBrands, setKanbanBrands] = useState<KanbanBrand[]>([]);
  const [kanbanLoaded, setKanbanLoaded] = useState(false);
  const [updatingKanbanId, setUpdatingKanbanId] = useState<string | null>(null);

  const fetchKanbanData = useCallback(async () => {
    try {
      // Peticions independents (no Promise.all) — si /kanban-phases falla (p.ex. la migracio
      // de project_kanban_phases encara no s'ha executat a la BD), els projectes/marques
      // igualment es carreguen i cada tarjeta cau al seu status com abans, en lloc de deixar
      // tot el tauler buit per un sol endpoint trencat.
      const projectsPromise = api.get<{ success: boolean; data: any[] }>('/taller/projects?status=active');
      const brandsPromise = api.get<{ success: boolean; data: KanbanBrand[] }>('/brands');
      const phasesPromise = api.get<{ success: boolean; data: { projectId: string; phase: string }[] }>('/taller/projects/kanban-phases')
        .catch((error) => {
          console.error('Error carregant les fases del Kanban (es continua amb status com a fallback):', error);
          return { success: false, data: [] as { projectId: string; phase: string }[] };
        });

      const [projectsRes, brandsRes, phasesRes] = await Promise.all([projectsPromise, brandsPromise, phasesPromise]);

      const phasesByProject = new Map<string, string[]>();
      for (const { projectId, phase } of phasesRes.data || []) {
        const arr = phasesByProject.get(projectId) ?? [];
        arr.push(phase);
        phasesByProject.set(projectId, arr);
      }
      setKanbanProjects((projectsRes.data || []).map((p) => ({
        id: p.id,
        projectNumber: p.projectNumber || p.project_number,
        expedientNumber: p.expedientNumber ?? p.expedient_number,
        name: p.name,
        brandId: p.brandId || p.brand_id,
        brandCode: p.brandCode || p.brand_code,
        clientName: p.clientName || p.client_name,
        expectedEndDate: p.expectedEndDate || p.expected_end_date,
        // Si el projecte encara no te cap fila a project_kanban_phases (no hauria de passar
        // despres del backfill, pero per seguretat), cau al seu status com abans.
        phases: phasesByProject.get(p.id) ?? [p.status],
      })));
      setKanbanBrands(brandsRes.data || []);
    } catch (error) {
      console.error('Error carregant el Kanban:', error);
    } finally {
      setKanbanLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (viewBy === 'kanban' && !kanbanLoaded) fetchKanbanData();
  }, [viewBy, kanbanLoaded, fetchKanbanData]);

  // Arrossegar a una columna nova AFEGEIX aquella fase sense treure les altres — una tarjeta
  // pot estar a vàries columnes alhora (2026-10-08).
  const handleKanbanAddPhase = async (project: KanbanProject, phase: string) => {
    if (project.phases.includes(phase)) return;
    setUpdatingKanbanId(project.id);
    const previousPhases = project.phases;
    setKanbanProjects((prev) => prev.map((p) => (p.id === project.id ? { ...p, phases: [...p.phases, phase] } : p)));
    try {
      await api.post(`/taller/projects/${project.id}/kanban-phases`, { phase });
    } catch (error) {
      console.error('Error afegint fase al Kanban:', error);
      setKanbanProjects((prev) => prev.map((p) => (p.id === project.id ? { ...p, phases: previousPhases } : p)));
    } finally {
      setUpdatingKanbanId(null);
    }
  };

  // Arrossegar fora del tauler treu nomes AQUELLA fase concreta — el backend rebutja deixar un
  // projecte sense cap fase activa.
  const handleKanbanRemovePhase = async (project: KanbanProject, phase: string) => {
    setUpdatingKanbanId(project.id);
    const previousPhases = project.phases;
    setKanbanProjects((prev) => prev.map((p) => (p.id === project.id ? { ...p, phases: p.phases.filter((ph) => ph !== phase) } : p)));
    try {
      await api.delete(`/taller/projects/${project.id}/kanban-phases/${phase}`);
    } catch (error) {
      console.error('Error traient fase del Kanban:', error);
      setKanbanProjects((prev) => prev.map((p) => (p.id === project.id ? { ...p, phases: previousPhases } : p)));
      alert('No es pot treure: un projecte ha de tenir sempre almenys una fase activa al Kanban.');
    } finally {
      setUpdatingKanbanId(null);
    }
  };

  // Nomes operaris de Taller o Muntatge — la resta (Oficina, etc.) no executen OT's de
  // fabricació/muntatge assignables des d'aquí.
  const employees = useMemo(() => allEmployees.filter((e) => e.departamentEnum === 'taller' || e.departamentEnum === 'muntatge'), [allEmployees]);

  // Vista "per operari": OT's de Treball i Muntatge (mà d'obra dels operaris) — Materials i
  // Serveis no s'hi planifiquen (es compren, no s'assignen a una persona).
  const ganttOTs = useMemo(() => allOTs.filter((ot) => ot.type === 'TRE' || ot.type === 'MUN'), [allOTs]);
  const ganttOTsById = useMemo(() => new Map(ganttOTs.map((ot) => [ot.id, ot])), [ganttOTs]);
  // Assignacions amb OT pendent (sense fetchAll complet encara) descartades per seguretat.
  const ganttAssignments = useMemo(() => assignments.filter((a) => ganttOTsById.has(a.workOrderId)), [assignments, ganttOTsById]);
  // Una OT es manté a "OT's pendents" independentment de quants operaris ja té assignats —
  // nomes desapareix quan es marca Completada/Cancel·lada (ja filtrat a fetchAll). Això permet
  // afegir-hi un segon (o tercer) operari a treballar-hi en paral·lel.
  const unassignedOTs = ganttOTs;

  // Dates efectives d'una OT: per Treball/Muntatge la font de veritat és work_order_assignments
  // (el Gantt per operari), no WorkOrder.plannedStartDate/plannedEndDate (que ningu escriu des
  // que una OT pot tenir-ne vàries assignacions en paral·lel) — es fusionen totes les seves
  // assignacions (min inici / max fi). Per Materials/Serveis (no planificables per operari) es
  // manté WorkOrder.plannedStartDate/plannedEndDate.
  const assignmentRangeByOT = useMemo(() => {
    const map = new Map<string, { start: string; end: string }>();
    for (const a of assignments) {
      const existing = map.get(a.workOrderId);
      if (!existing) {
        map.set(a.workOrderId, { start: a.plannedStartDate, end: a.plannedEndDate });
      } else {
        map.set(a.workOrderId, {
          start: existing.start < a.plannedStartDate ? existing.start : a.plannedStartDate,
          end: existing.end > a.plannedEndDate ? existing.end : a.plannedEndDate,
        });
      }
    }
    return map;
  }, [assignments]);

  const effectiveRange = useCallback(
    (ot: GanttOT): { start: string; end: string } | null => {
      if (ot.type === 'TRE' || ot.type === 'MUN') {
        return assignmentRangeByOT.get(ot.id) || null;
      }
      return ot.plannedStartDate ? { start: ot.plannedStartDate, end: ot.plannedEndDate || ot.plannedStartDate } : null;
    },
    [assignmentRangeByOT]
  );

  // Vista "capacitat": hores previstes per equip i setmana (repartides uniformement entre els
  // dies que ocupa cada OT) vs. capacitat disponible (suma de weeklyHours dels actius de l'equip).
  const weekStarts = useMemo(() => {
    const firstMonday = mondayOfWeek(rangeStart);
    return Array.from({ length: WEEKS_VISIBLE }, (_, i) => addDaysISO(firstMonday, i * 7));
  }, [rangeStart]);

  const capacityData = useMemo(() => {
    const disponible: Record<EquipKey, number> = { OFICINA: 0, TALLER: 0, MUNTATGE: 0 };
    for (const emp of allEmployees) {
      const equip = emp.departamentEnum ? EQUIP_OF_DEPARTAMENT[emp.departamentEnum] : undefined;
      if (equip) disponible[equip] += Number(emp.weeklyHours) || 0;
    }

    const planificades: Record<EquipKey, number[]> = {
      OFICINA: weekStarts.map(() => 0), TALLER: weekStarts.map(() => 0), MUNTATGE: weekStarts.map(() => 0),
    };
    const rangeFirstDay = weekStarts[0];
    const rangeLastDay = addDaysISO(weekStarts[weekStarts.length - 1], 6);

    for (const ot of allOTs) {
      const fase = FASE_OF[ot.type || ''];
      if (!fase || fase === 'COMPRES') continue;
      const range = effectiveRange(ot);
      if (!range) continue;
      const equip = fase as EquipKey;
      const start = range.start;
      const end = range.end;
      if (end < rangeFirstDay || start > rangeLastDay) continue;
      const totalDies = Math.max(1, diffDaysISO(start, end) + 1);
      const horesPerDia = (Number(ot.prevHours) || 0) / totalDies;
      for (let i = 0; i < totalDies; i++) {
        const dia = addDaysISO(start, i);
        if (dia < rangeFirstDay || dia > rangeLastDay) continue;
        const weekIdx = Math.floor(diffDaysISO(rangeFirstDay, dia) / 7);
        if (weekIdx >= 0 && weekIdx < WEEKS_VISIBLE) planificades[equip][weekIdx] += horesPerDia;
      }
    }

    return { disponible, planificades };
  }, [allEmployees, allOTs, weekStarts, effectiveRange]);

  const days = useMemo(() => {
    const arr: { iso: string; date: Date }[] = [];
    for (let i = 0; i < VISIBLE_DAYS; i++) {
      const iso = addDaysISO(rangeStart, i);
      arr.push({ iso, date: new Date(iso + 'T00:00:00') });
    }
    return arr;
  }, [rangeStart]);
  const rangeEnd = days[days.length - 1].iso;
  const todayISO = toISO(new Date());

  const assignmentsForEmployee = useCallback(
    (employeeId: string) =>
      ganttAssignments
        .filter((a) => a.employeeId === employeeId && a.plannedStartDate <= rangeEnd && a.plannedEndDate >= rangeStart)
        .map((a) => ({ assignment: a, ot: ganttOTsById.get(a.workOrderId)! }))
        .filter((row) => !!row.ot),
    [ganttAssignments, ganttOTsById, rangeStart, rangeEnd]
  );

  // ── Vista "Operaris 2": mateixa font de veritat que "Per operari" (ganttOTs/
  // ganttAssignments de Treball+Muntatge), pero amb les files invertides — cada
  // fila és un expedient comercial (etiqueta) i les tarjetes que s'arrosseguen
  // són els operaris (en lloc de les OT's). Com un expedient pot tenir vàries
  // OT's de Treball/Muntatge, cal triar a quina s'assigna l'operari arrossegat.
  const employeesById = useMemo(() => new Map(allEmployees.map((e) => [e.id, e])), [allEmployees]);
  // Una fila per CADA expedient de taller actiu (no nomes els que ja tenen una OT de
  // Treball/Muntatge creada) — així es pot arrossegar un operari a qualsevol expedient.
  const projectOperariRows = useMemo(() => {
    const otsByProject = new Map<string, GanttOT[]>();
    for (const ot of ganttOTs) {
      if (!ot.projectId) continue;
      const arr = otsByProject.get(ot.projectId) ?? [];
      arr.push(ot);
      otsByProject.set(ot.projectId, arr);
    }
    return allTallerProjects
      .map((p) => ({
        projectId: p.id,
        etiqueta: p.etiquetaExpedient || p.name || '—',
        expedientNumber: p.expedientNumber,
        ots: otsByProject.get(p.id) ?? [],
      }))
      .sort((a, b) => a.etiqueta.localeCompare(b.etiqueta));
  }, [allTallerProjects, ganttOTs]);

  const assignmentsForProject = useCallback(
    (ots: GanttOT[]) => {
      const otIds = new Set(ots.map((ot) => ot.id));
      return ganttAssignments
        .filter((a) => otIds.has(a.workOrderId) && a.plannedStartDate <= rangeEnd && a.plannedEndDate >= rangeStart)
        .map((a) => ({ assignment: a, ot: ganttOTsById.get(a.workOrderId)! }))
        .filter((row) => !!row.ot);
    },
    [ganttAssignments, ganttOTsById, rangeStart, rangeEnd]
  );

  // ── Drag (reprogramar/reasignar) i resize (durada) ──────────────────────
  // Les columnes de dia són percentuals (100%/VISIBLE_DAYS, via CSS flex),
  // no ample fix en px — aixi la graella sempre ocupa tot l'ample disponible
  // sense dependre de mesurar-lo amb JS. Per als gestos (drop/resize),
  // l'ample de dia en px es calcula sobre la marxa amb getBoundingClientRect.
  // Draggable: o bé una OT pendent (nova assignació) o bé una barra ja col·locada (moure-la).
  type DragPayload = { kind: 'new'; ot: GanttOT } | { kind: 'move'; assignment: GanttAssignment; ot: GanttOT };
  const draggingRef = useRef<DragPayload | null>(null);
  const [resizing, setResizing] = useState<{ assignment: GanttAssignment; previewEnd: string } | null>(null);
  const resizeStartX = useRef(0);

  // Dia sobre el que es passa l'arrossegament — pinta una columna de guia
  // perque es vegi exactament on caurà en soltar (el drag natiu HTML5 no
  // dona cap feedback visual propi mentre s'arrossega sobre la graella).
  const [dragOverCell, setDragOverCell] = useState<{ rowKey: string; dayIndex: number } | null>(null);
  const dayIndexFromEvent = (e: React.DragEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const dayPx = rect.width / VISIBLE_DAYS;
    return Math.max(0, Math.min(VISIBLE_DAYS - 1, Math.floor((e.clientX - rect.left) / dayPx)));
  };

  const persistAssignment = async (
    assignmentId: string | null,
    workOrderId: string,
    patch: { employeeId?: string; plannedStartDate?: string; plannedEndDate?: string; phaseName?: string | null }
  ) => {
    try {
      if (assignmentId) {
        await api.put(`/work-orders/assignments/${assignmentId}`, patch);
      } else {
        await api.post(`/work-orders/${workOrderId}/assignments`, patch);
      }
      await fetchAll(true);
    } catch (error) {
      console.error("Error desant l'assignació:", error);
      alert("Error en desar l'assignació. Torna-ho a intentar.");
    }
  };

  const deleteAssignment = async (assignmentId: string) => {
    try {
      await api.delete(`/work-orders/assignments/${assignmentId}`);
      await fetchAll(true);
    } catch (error) {
      console.error("Error traient l'assignació:", error);
      alert("Error en treure l'assignació. Torna-ho a intentar.");
    }
  };

  // Un mateix operari pot tenir vàries assignacions amb dates que se solapin — treball en
  // paral·lel entre OT's diferents, o vàries fases de la mateixa OT (2026-10-08, abans es
  // bloquejava amb findOverlap).
  const handleDropOnRow = (e: React.DragEvent<HTMLDivElement>, employeeId: string) => {
    e.preventDefault();
    setDragOverCell(null);
    const payload = draggingRef.current;
    draggingRef.current = null;
    if (!payload) return;
    const { ot } = payload;
    const dayIndex = dayIndexFromEvent(e);
    const newStart = addDaysISO(rangeStart, dayIndex);
    const existingStart = payload.kind === 'move' ? payload.assignment.plannedStartDate : ot.plannedStartDate;
    const existingEnd = payload.kind === 'move' ? payload.assignment.plannedEndDate : ot.plannedEndDate;
    const duration = existingStart && existingEnd ? diffDaysISO(existingStart, existingEnd) : 0;
    const newEnd = addDaysISO(newStart, Math.max(0, duration));
    if (payload.kind === 'move') {
      persistAssignment(payload.assignment.id, ot.id, { employeeId, plannedStartDate: newStart, plannedEndDate: newEnd });
    } else {
      // Nom de fase opcional a cada arrossegament (es pot deixar en blanc) — aixi tambe es pot
      // posar nom a la primera assignació d'una OT, no nomes a partir de la segona.
      const phaseName = prompt(`Nom per a aquesta fase de l'OT ${ot.orderNumber} (opcional):`) || undefined;
      persistAssignment(null, ot.id, { employeeId, plannedStartDate: newStart, plannedEndDate: newEnd, phaseName });
    }
  };

  const handleResizeMouseDown = (e: React.MouseEvent, assignment: GanttAssignment, ot: GanttOT) => {
    e.preventDefault();
    e.stopPropagation();
    // handle -> barra -> fila (la fila té l'ample real de la graella)
    const rowEl = (e.currentTarget as HTMLElement).parentElement?.parentElement as HTMLElement | null;
    const dayPx = (rowEl?.getBoundingClientRect().width || 1) / VISIBLE_DAYS;
    resizeStartX.current = e.clientX;
    const initialEnd = assignment.plannedEndDate;
    setResizing({ assignment, previewEnd: initialEnd });

    const onMove = (ev: MouseEvent) => {
      const deltaDays = Math.round((ev.clientX - resizeStartX.current) / dayPx);
      const start = assignment.plannedStartDate;
      let newEnd = addDaysISO(initialEnd, deltaDays);
      if (newEnd < start) newEnd = start;
      setResizing({ assignment, previewEnd: newEnd });
    };
    const onUp = (ev: MouseEvent) => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      const deltaDays = Math.round((ev.clientX - resizeStartX.current) / dayPx);
      const start = assignment.plannedStartDate;
      let newEnd = addDaysISO(initialEnd, deltaDays);
      if (newEnd < start) newEnd = start;
      setResizing(null);
      if (newEnd !== initialEnd) {
        persistAssignment(assignment.id, ot.id, { plannedEndDate: newEnd });
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // ── Drag per a la vista "Operaris 2" (inversa de la de "Per operari": aquí
  // s'arrosseguen operaris cap a la fila d'un expedient) — estat separat perquè
  // el payload que es transporta és un operari, no una OT.
  type DragPayload2 = { kind: 'new'; employee: EmployeeOption } | { kind: 'move'; assignment: GanttAssignment; ot: GanttOT };
  const draggingRef2 = useRef<DragPayload2 | null>(null);

  // Quan l'expedient on es deixa anar l'operari te mes d'una OT (Treball/
  // Muntatge), cal triar a quina s'assigna — abans amb un prompt() de text
  // ("escriu 1 o 2"), ara amb un selector real (botons).
  const [otChoicePrompt, setOtChoicePrompt] = useState<{
    etiqueta: string;
    employee: EmployeeOption;
    ots: GanttOT[];
    newStart: string;
  } | null>(null);

  const assignToOt = (targetOt: GanttOT, employee: EmployeeOption, newStart: string) => {
    const newEnd = newStart;
    const phaseName = prompt(`Nom per a aquesta fase de l'OT ${targetOt.orderNumber} (opcional):`) || undefined;
    persistAssignment(null, targetOt.id, { employeeId: employee.id, plannedStartDate: newStart, plannedEndDate: newEnd, phaseName });
  };

  const handleDropOnProjectRow = (
    e: React.DragEvent<HTMLDivElement>,
    row: { projectId: string; etiqueta: string; ots: GanttOT[] }
  ) => {
    e.preventDefault();
    setDragOverCell(null);
    const payload = draggingRef2.current;
    draggingRef2.current = null;
    if (!payload) return;
    const dayIndex = dayIndexFromEvent(e);
    const newStart = addDaysISO(rangeStart, dayIndex);

    if (payload.kind === 'move') {
      const duration = diffDaysISO(payload.assignment.plannedStartDate, payload.assignment.plannedEndDate);
      const newEnd = addDaysISO(newStart, Math.max(0, duration));
      persistAssignment(payload.assignment.id, payload.ot.id, { plannedStartDate: newStart, plannedEndDate: newEnd });
      return;
    }

    // Arrossegament nou: cal saber a quina OT (de Treball/Muntatge) d'aquest
    // expedient s'assigna l'operari — directe si nomes n'hi ha una, si no es
    // selecciona des del modal (otChoicePrompt).
    if (row.ots.length === 0) {
      alert(`"${row.etiqueta}" encara no té cap OT de Treball/Muntatge creada — crea-la primer per poder-hi assignar operaris.`);
      return;
    }
    if (row.ots.length === 1) {
      assignToOt(row.ots[0], payload.employee, newStart);
      return;
    }
    setOtChoicePrompt({ etiqueta: row.etiqueta, employee: payload.employee, ots: row.ots, newStart });
  };

  const otColor = (type?: string) => {
    if (type === 'MAT') return 'bg-blue-100 border-blue-300 text-blue-800';
    if (type === 'SRV') return 'bg-purple-100 border-purple-300 text-purple-800';
    if (type === 'TRE') return 'bg-emerald-100 border-emerald-300 text-emerald-800';
    if (type === 'MUN') return 'bg-pink-100 border-pink-300 text-pink-800';
    return 'bg-gray-100 border-gray-300 text-gray-800';
  };

  return (
    <div className="p-5">
      {loading ? (
        <div className="py-12 text-center text-sm text-muted-foreground">Carregant...</div>
      ) : (
        <div className="flex gap-4 items-start">
          {/* Gantt */}
          <div className="flex-1 min-w-0 bg-white border border-gray-200 rounded-lg">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CalendarRange size={16} className="text-orange-600" />
                <h3 className="text-sm font-semibold text-gray-800">
                  Planificació — {rangeStart} a {rangeEnd}
                </h3>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
                  <button
                    onClick={() => setViewBy('operari')}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${viewBy === 'operari' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    <Users size={13} /> Per operari
                  </button>
                  <button
                    onClick={() => setViewBy('operari2')}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${viewBy === 'operari2' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    <Users2 size={13} /> Per Projectes
                  </button>
                  <button
                    onClick={() => setViewBy('capacitat')}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${viewBy === 'capacitat' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    <Gauge size={13} /> Capacitat
                  </button>
                  <button
                    onClick={() => setViewBy('kanban')}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${viewBy === 'kanban' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    <Trello size={13} /> Kanban
                  </button>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setRangeStart((s) => addDaysISO(s, -7))} className="p-1.5 rounded hover:bg-gray-100 text-gray-500">
                    <ChevronLeft size={16} />
                  </button>
                  <button onClick={() => setRangeStart(toISO(new Date()))} className="px-2.5 py-1 rounded text-xs font-medium hover:bg-gray-100 text-gray-600">
                    Avui
                  </button>
                  <button onClick={() => setRangeStart((s) => addDaysISO(s, 7))} className="p-1.5 rounded hover:bg-gray-100 text-gray-500">
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </div>

            {(viewBy === 'operari' || viewBy === 'operari2') && (
            <>
            {/* Capçalera de dies */}
            <div className="flex border-b border-gray-200">
              <div style={{ width: EMPLOYEE_COL_WIDTH }} className="shrink-0 px-3 py-2 text-xs font-medium text-muted-foreground border-r border-gray-200">
                {viewBy === 'operari' ? 'Operari' : 'Expedient'}
              </div>
              <div className="flex flex-1 min-w-0">
                {days.map(({ iso, date }) => {
                  const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                  const isToday = iso === todayISO;
                  const showMonth = date.getDate() === 1 || iso === rangeStart;
                  return (
                    <div
                      key={iso}
                      className={`flex-1 min-w-0 px-0.5 py-1 text-center text-[10px] border-r border-gray-100 ${isWeekend ? 'bg-gray-50' : ''} ${isToday ? 'bg-blue-50 font-semibold text-blue-700' : 'text-muted-foreground'}`}
                    >
                      <div>{WEEKDAY_LABELS[date.getDay()]}</div>
                      <div>{date.getDate()}{showMonth ? ` ${MONTH_LABELS[date.getMonth()]}` : ''}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Files d'operaris */}
            {viewBy === 'operari' && (employees.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">No hi ha operaris actius</div>
            ) : (
              employees.map((emp) => {
                const empRows = assignmentsForEmployee(emp.id);
                const laneOf = assignLanes(empRows, (r) => r.assignment.plannedStartDate, (r) => r.assignment.plannedEndDate);
                const rowHeight = GANTT_ROW_PAD + Math.max(1, ...laneOf.map((l) => l + 1)) * GANTT_LANE_HEIGHT;
                return (
                  <div key={emp.id} className="flex border-b border-gray-100">
                    <div style={{ width: EMPLOYEE_COL_WIDTH }} className="shrink-0 px-3 py-2 text-xs font-medium border-r border-gray-200 flex items-center">
                      {emp.firstName} {emp.lastName}
                    </div>
                    <div
                      className="relative flex-1 min-w-0"
                      style={{ height: rowHeight }}
                      onDragOver={(e) => { e.preventDefault(); setDragOverCell({ rowKey: `emp:${emp.id}`, dayIndex: dayIndexFromEvent(e) }); }}
                      onDragLeave={() => setDragOverCell((prev) => (prev?.rowKey === `emp:${emp.id}` ? null : prev))}
                      onDrop={(e) => handleDropOnRow(e, emp.id)}
                    >
                      {/* Fons: quadricula de dies + cap de setmana ombrejat — i guia del
                          dia sobre el que es passa un arrossegament, perque es vegi
                          exactament on caurà en soltar. */}
                      <div className="absolute inset-0 flex pointer-events-none">
                        {days.map(({ iso, date }, di) => {
                          const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                          const isToday = iso === todayISO;
                          const isDragOver = dragOverCell?.rowKey === `emp:${emp.id}` && dragOverCell.dayIndex === di;
                          return (
                            <div
                              key={iso}
                              className={`flex-1 min-w-0 h-full border-r border-gray-100 ${isWeekend ? 'bg-gray-50' : ''} ${isToday ? 'bg-blue-50/60' : ''} ${isDragOver ? 'bg-amber-200/70 ring-2 ring-inset ring-amber-400' : ''}`}
                            />
                          );
                        })}
                      </div>

                      {/* Barres de les assignacions d'aquest operari — una barra per assignació,
                          no per OT (una mateixa OT pot tenir-ne vàries, una per operari). */}
                      {empRows.map(({ assignment, ot }, i) => {
                        const isResizingThis = resizing?.assignment.id === assignment.id;
                        const start = assignment.plannedStartDate;
                        const end = isResizingThis ? resizing!.previewEnd : assignment.plannedEndDate;
                        const clippedStart = start < rangeStart ? rangeStart : start;
                        const clippedEnd = end > rangeEnd ? rangeEnd : end;
                        const leftPct = (diffDaysISO(rangeStart, clippedStart) / VISIBLE_DAYS) * 100;
                        const widthPct = (Math.max(1, diffDaysISO(clippedStart, clippedEnd) + 1) / VISIBLE_DAYS) * 100;
                        return (
                          <div
                            key={assignment.id}
                            draggable
                            onDragStart={() => { draggingRef.current = { kind: 'move', assignment, ot }; }}
                            onDragEnd={() => { draggingRef.current = null; setDragOverCell(null); }}
                            onDoubleClick={(e) => {
                              e.stopPropagation();
                              const name = prompt('Nom de la fase (deixa en blanc per treure\'l):', assignment.phaseName || '');
                              if (name !== null) persistAssignment(assignment.id, ot.id, { phaseName: name });
                            }}
                            className={`absolute h-8 rounded border px-1.5 flex items-center gap-1 text-[10px] cursor-grab active:cursor-grabbing overflow-hidden group ${otColor(ot.type)}`}
                            style={{ left: `${leftPct}%`, width: `calc(${widthPct}% - 4px)`, minWidth: '62px', top: GANTT_ROW_PAD + laneOf[i] * GANTT_LANE_HEIGHT }}
                            title={`${ot.orderNumber} — ${ot.name}${assignment.phaseName ? ` [${assignment.phaseName}]` : ''}${ot.project ? ` (${ot.project.expedientNumber ?? ''} — ${ot.project.name ?? ''})` : ''} (${Number(ot.prevHours || 0).toFixed(1)}h) — doble clic per posar/canviar el nom de la fase`}
                          >
                            <span className="font-mono font-semibold shrink-0">{ot.orderNumber}</span>
                            {assignment.phaseName && (
                              <span className="font-semibold shrink-0 italic">[{assignment.phaseName}]</span>
                            )}
                            <span className="truncate min-w-0">{ot.name}</span>
                            {ot.project && <span className="truncate min-w-0 opacity-75">— {ot.project.etiquetaExpedient || ot.project.name}</span>}
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); deleteAssignment(assignment.id); }}
                              className="absolute top-0 right-2 text-[10px] leading-none opacity-0 group-hover:opacity-100 hover:text-red-700 px-0.5"
                              title="Treure aquest operari de l'OT"
                            >
                              ×
                            </button>
                            <div
                              onMouseDown={(e) => handleResizeMouseDown(e, assignment, ot)}
                              draggable={false}
                              onDragStart={(e) => e.preventDefault()}
                              className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-black/10"
                              title="Arrossega per canviar la durada"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            ))}

            {/* Files d'expedients — vista "Operaris 2": inversa de "Per operari", aquí
                s'arrosseguen operaris (des de la barra lateral) cap a la fila del seu
                expedient; cada barra representa una assignació (operari+OT+dates), igual
                que a "Per operari" pero etiquetada amb el nom de l'operari. */}
            {viewBy === 'operari2' && (projectOperariRows.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">Cap expedient amb OT&apos;s de Treball/Muntatge pendents</div>
            ) : (
              projectOperariRows.map((row) => {
                const rows = assignmentsForProject(row.ots);
                const laneOf = assignLanes(rows, (r) => r.assignment.plannedStartDate, (r) => r.assignment.plannedEndDate);
                const rowHeight = GANTT_ROW_PAD + Math.max(1, ...laneOf.map((l) => l + 1)) * GANTT_LANE_HEIGHT;
                return (
                  <div key={row.projectId} className="flex border-b border-gray-100">
                    <div
                      style={{ width: EMPLOYEE_COL_WIDTH }}
                      className="shrink-0 px-3 py-2 text-xs font-medium border-r border-gray-200 flex items-center cursor-pointer hover:bg-gray-50"
                      onClick={() => navigate(`/taller/expedients/${row.projectId}`)}
                      title={row.etiqueta}
                    >
                      <span className="truncate">{row.etiqueta}</span>
                    </div>
                    <div
                      className="relative flex-1 min-w-0"
                      style={{ height: rowHeight }}
                      onDragOver={(e) => { e.preventDefault(); setDragOverCell({ rowKey: `proj:${row.projectId}`, dayIndex: dayIndexFromEvent(e) }); }}
                      onDragLeave={() => setDragOverCell((prev) => (prev?.rowKey === `proj:${row.projectId}` ? null : prev))}
                      onDrop={(e) => handleDropOnProjectRow(e, row)}
                    >
                      {/* Fons: quadricula de dies + cap de setmana ombrejat — i guia del dia */}
                      <div className="absolute inset-0 flex pointer-events-none">
                        {days.map(({ iso, date }, di) => {
                          const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                          const isToday = iso === todayISO;
                          const isDragOver = dragOverCell?.rowKey === `proj:${row.projectId}` && dragOverCell.dayIndex === di;
                          return (
                            <div
                              key={iso}
                              className={`flex-1 min-w-0 h-full border-r border-gray-100 ${isWeekend ? 'bg-gray-50' : ''} ${isToday ? 'bg-blue-50/60' : ''} ${isDragOver ? 'bg-amber-200/70 ring-2 ring-inset ring-amber-400' : ''}`}
                            />
                          );
                        })}
                      </div>

                      {/* Barres de les assignacions d'aquest expedient — etiquetades amb
                          el nom de l'operari (en lloc del número d'OT). */}
                      {rows.map(({ assignment, ot }, i) => {
                        const emp = employeesById.get(assignment.employeeId);
                        const isResizingThis = resizing?.assignment.id === assignment.id;
                        const start = assignment.plannedStartDate;
                        const end = isResizingThis ? resizing!.previewEnd : assignment.plannedEndDate;
                        const clippedStart = start < rangeStart ? rangeStart : start;
                        const clippedEnd = end > rangeEnd ? rangeEnd : end;
                        const leftPct = (diffDaysISO(rangeStart, clippedStart) / VISIBLE_DAYS) * 100;
                        const widthPct = (Math.max(1, diffDaysISO(clippedStart, clippedEnd) + 1) / VISIBLE_DAYS) * 100;
                        return (
                          <div
                            key={assignment.id}
                            draggable
                            onDragStart={() => { draggingRef2.current = { kind: 'move', assignment, ot }; }}
                            onDragEnd={() => { draggingRef2.current = null; setDragOverCell(null); }}
                            onDoubleClick={(e) => {
                              e.stopPropagation();
                              const name = prompt('Nom de la fase (deixa en blanc per treure\'l):', assignment.phaseName || '');
                              if (name !== null) persistAssignment(assignment.id, ot.id, { phaseName: name });
                            }}
                            className={`absolute h-8 rounded border px-1.5 flex items-center gap-1 text-[10px] cursor-grab active:cursor-grabbing overflow-hidden group ${otColor(ot.type)}`}
                            style={{ left: `${leftPct}%`, width: `calc(${widthPct}% - 4px)`, minWidth: '62px', top: GANTT_ROW_PAD + laneOf[i] * GANTT_LANE_HEIGHT }}
                            title={`${emp ? `${emp.firstName} ${emp.lastName}` : 'Operari'} — ${ot.orderNumber} ${ot.name}${assignment.phaseName ? ` [${assignment.phaseName}]` : ''} (${Number(ot.prevHours || 0).toFixed(1)}h) — doble clic per posar/canviar el nom de la fase`}
                          >
                            <span className="font-semibold shrink-0 truncate">{emp ? `${emp.firstName} ${emp.lastName}` : '—'}</span>
                            {assignment.phaseName && (
                              <span className="font-semibold shrink-0 italic">[{assignment.phaseName}]</span>
                            )}
                            <span className="font-mono shrink-0 opacity-75">{ot.orderNumber}</span>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); deleteAssignment(assignment.id); }}
                              className="absolute top-0 right-2 text-[10px] leading-none opacity-0 group-hover:opacity-100 hover:text-red-700 px-0.5"
                              title="Treure aquest operari de l'OT"
                            >
                              ×
                            </button>
                            <div
                              onMouseDown={(e) => handleResizeMouseDown(e, assignment, ot)}
                              draggable={false}
                              onDragStart={(e) => e.preventDefault()}
                              className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-black/10"
                              title="Arrossega per canviar la durada"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            ))}

            </>
            )}

            {/* Vista "Capacitat de recursos": taula setmanal per equip */}
            {viewBy === 'capacitat' && (
              <div className="overflow-x-auto p-3">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr>
                      <th className="text-left px-2 py-1.5 font-medium text-muted-foreground w-44">Equip / Setmana</th>
                      {weekStarts.map((ws) => (
                        <th key={ws} className="text-center px-2 py-1.5 font-medium text-muted-foreground">
                          {isoWeekLabel(ws)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(['OFICINA', 'TALLER', 'MUNTATGE'] as EquipKey[]).map((equip) => (
                      <Fragment key={equip}>
                        <tr className="bg-gray-100">
                          <td colSpan={weekStarts.length + 1} className="px-2 py-1 font-semibold text-gray-700">
                            {EQUIP_LABEL[equip]}
                          </td>
                        </tr>
                        <tr key={`${equip}-disponible`} className="border-b border-gray-100">
                          <td className="px-2 py-1 pl-4 text-muted-foreground">Capacitat disponible</td>
                          {weekStarts.map((ws) => (
                            <td key={ws} className="text-center px-2 py-1">{capacityData.disponible[equip].toFixed(1)}</td>
                          ))}
                        </tr>
                        <tr key={`${equip}-planificades`} className="border-b border-gray-100">
                          <td className="px-2 py-1 pl-4 text-muted-foreground">Hores planificades</td>
                          {capacityData.planificades[equip].map((h, i) => (
                            <td key={weekStarts[i]} className="text-center px-2 py-1">{h.toFixed(1)}</td>
                          ))}
                        </tr>
                        <tr key={`${equip}-percent`} className="border-b-2 border-gray-200">
                          <td className="px-2 py-1 pl-4 text-muted-foreground">% càrrega</td>
                          {capacityData.planificades[equip].map((h, i) => {
                            const disp = capacityData.disponible[equip];
                            const pct = disp > 0 ? (h / disp) * 100 : 0;
                            const colorCls = pct > 110 ? 'bg-red-100 text-red-700' : pct >= 90 ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700';
                            return (
                              <td key={weekStarts[i]} className="text-center px-2 py-1">
                                <span className={`inline-block px-1.5 py-0.5 rounded font-medium ${colorCls}`}>
                                  {disp > 0 ? `${Math.round(pct)}%` : '—'}
                                </span>
                              </td>
                            );
                          })}
                        </tr>
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Vista "Kanban": tauler per fase de seguiment (ProjectStatus) — moguda aquí des
                d'Expedients de Taller (2026-10-08). */}
            {viewBy === 'kanban' && (
              <div className="p-3">
                {!kanbanLoaded ? (
                  <p className="text-sm text-muted-foreground py-8 text-center">Carregant...</p>
                ) : (
                  <ProjectsKanbanBoard
                    projects={kanbanProjects}
                    brands={kanbanBrands}
                    onCardClick={(project) => navigate(`/taller/expedients/${project.id}`)}
                    onAddPhase={handleKanbanAddPhase}
                    onRemovePhase={handleKanbanRemovePhase}
                    updatingId={updatingKanbanId}
                  />
                )}
              </div>
            )}
          </div>

          {/* OT's sense assignar — arrossega a una fila del Gantt per assignar (nomes vista per operari) */}
          {viewBy === 'operari' && (
          <div className="w-72 shrink-0 bg-white border border-gray-200 rounded-lg">
            <div className="px-3 py-3 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-800">
                OT&apos;s pendents ({unassignedOTs.length})
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">Arrossega-les al Gantt per assignar-les</p>
            </div>
            <div className="p-3 space-y-1.5 max-h-[70vh] overflow-y-auto">
              {unassignedOTs.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">Cap OT pendent d&apos;assignar.</p>
              ) : (
                unassignedOTs.map((ot) => {
                  const otAssignments = ganttAssignments.filter((a) => a.workOrderId === ot.id);
                  // Operaris DIFERENTS assignats — un mateix operari amb vàries fases de la
                  // mateixa OT compta com 1, no com una per fase (2026-10-08).
                  const assignedEmployeeCount = new Set(otAssignments.map((a) => a.employeeId)).size;
                  // Hores assignades fins ara via arrossegaments — 8h per dia i assignació (no
                  // es deduplica si dos operaris cobreixen el mateix dia: cadascun hi aporta les
                  // seves 8h).
                  const assignedHours = otAssignments.reduce(
                    (sum, a) => sum + (diffDaysISO(a.plannedStartDate, a.plannedEndDate) + 1) * 8,
                    0
                  );
                  const prevHours = Number(ot.prevHours || 0);
                  return (
                    <div
                      key={ot.id}
                      draggable
                      onDragStart={() => { draggingRef.current = { kind: 'new', ot }; }}
                      onDragEnd={() => { draggingRef.current = null; setDragOverCell(null); }}
                      className="px-2 py-1.5 bg-white border border-gray-300 rounded shadow-sm text-[10px] cursor-grab active:cursor-grabbing"
                      title={`${ot.orderNumber} — ${ot.name}${ot.project ? ` (${ot.project.expedientNumber ?? ''} — ${ot.project.name ?? ''})` : ''}`}
                    >
                      {/* Línia superior: num OT + tipus + Etiqueta del projecte */}
                      <div className="flex items-center justify-between gap-1">
                        <span className="flex items-center gap-1 min-w-0">
                          <span className="font-mono text-blue-600 shrink-0">{ot.orderNumber}</span>
                          <span className="truncate text-gray-600">{ot.name}</span>
                        </span>
                        {ot.project && (
                          <span className="truncate text-muted-foreground shrink-0" title={ot.project.name}>
                            {ot.project.etiquetaExpedient || ot.project.name}
                          </span>
                        )}
                      </div>
                      {/* Línia inferior: num operaris + hores previstes + hores assignades */}
                      <div className="mt-0.5 flex items-center justify-between gap-1">
                        <span className={assignedEmployeeCount > 0 ? 'text-emerald-700 font-medium' : 'text-muted-foreground'}>
                          {assignedEmployeeCount > 0 ? `${assignedEmployeeCount} operari${assignedEmployeeCount > 1 ? 's' : ''}` : 'Sense operari'}
                        </span>
                        <span className="text-muted-foreground">
                          {prevHours.toFixed(1)}h
                          {assignedHours > 0 && (
                            <span className={assignedHours > prevHours ? 'text-amber-600 font-semibold' : 'text-emerald-600'}>
                              {' '}({assignedHours.toFixed(1)}h)
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
          )}

          {/* Operaris — arrossega'ls a la fila d'un expedient per assignar-los (nomes vista Operaris 2) */}
          {viewBy === 'operari2' && (
          <div className="w-72 shrink-0 bg-white border border-gray-200 rounded-lg">
            <div className="px-3 py-3 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-800">
                Operaris ({employees.length})
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">Arrossega&apos;ls a la fila d&apos;un expedient per assignar-los</p>
            </div>
            <div className="p-3 space-y-1.5 max-h-[70vh] overflow-y-auto">
              {employees.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">No hi ha operaris actius.</p>
              ) : (
                employees.map((emp) => {
                  const empAssignments = ganttAssignments.filter((a) => a.employeeId === emp.id);
                  const assignedProjectCount = new Set(
                    empAssignments.map((a) => ganttOTsById.get(a.workOrderId)?.projectId).filter(Boolean)
                  ).size;
                  return (
                    <div
                      key={emp.id}
                      draggable
                      onDragStart={() => { draggingRef2.current = { kind: 'new', employee: emp }; }}
                      onDragEnd={() => { draggingRef2.current = null; setDragOverCell(null); }}
                      className="px-2 py-1.5 bg-white border border-gray-300 rounded shadow-sm text-[10px] cursor-grab active:cursor-grabbing"
                      title={`${emp.firstName} ${emp.lastName}`}
                    >
                      <div className="font-medium text-gray-800 truncate">{emp.firstName} {emp.lastName}</div>
                      <div className="mt-0.5 text-muted-foreground">
                        {assignedProjectCount > 0 ? `${assignedProjectCount} expedient${assignedProjectCount > 1 ? 's' : ''}` : 'Sense expedient'}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
          )}
        </div>
      )}

      {/* Selecció d'OT quan l'expedient en te mes d'una (Treball/Muntatge) */}
      {otChoicePrompt && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
          onClick={() => setOtChoicePrompt(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-[380px] p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-sm font-semibold text-gray-900 mb-1">
              A quina OT de "{otChoicePrompt.etiqueta}" assignes {otChoicePrompt.employee.firstName} {otChoicePrompt.employee.lastName}?
            </h3>
            <div className="mt-3 space-y-1.5">
              {otChoicePrompt.ots.map((ot) => (
                <button
                  key={ot.id}
                  type="button"
                  onClick={() => {
                    const { employee, newStart } = otChoicePrompt;
                    setOtChoicePrompt(null);
                    assignToOt(ot, employee, newStart);
                  }}
                  className="w-full text-left px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors text-xs"
                >
                  <span className="font-medium text-gray-900">{ot.orderNumber}</span>
                  <span className="text-muted-foreground"> — {ot.name}</span>
                </button>
              ))}
            </div>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setOtChoicePrompt(null)}
                className="px-3 py-1.5 text-xs text-muted-foreground hover:bg-gray-100 rounded transition-colors"
              >
                Cancel·lar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
